/**
 * ProShip Design System — Patterns Layer (barrel export)
 */
import {FONT, SIZE, WEIGHT} from '../foundation/typography'
import {NAVY, BORDER, LIGHT_BG, SUBTLE, WHITE, PRIMARY_BLUE} from '../foundation/colors'
import {RADIUS, PAD, GAP} from '../foundation/spacing'

// ── KPI Card ────────────────────────────────────────────────────
export const kpiCardSx = (accent = PRIMARY_BLUE) => ({
  p: 2.5,
  borderRadius: '14px',
  border: `1px solid ${BORDER}`,
  borderLeft: `4px solid ${accent}`,
  bgcolor: WHITE,
  height: '100%',
  transition: 'box-shadow 0.2s',
  '&:hover': {boxShadow: '0 4px 16px rgba(0,0,0,0.08)'},
})

export const kpiValueSx = (color = NAVY) => ({
  fontFamily: FONT,
  fontWeight: WEIGHT.extra,
  fontSize: SIZE['3xl'],
  color,
  lineHeight: 1.2,
})

export const kpiLabelSx = {
  fontFamily: FONT,
  fontWeight: WEIGHT.semibold,
  fontSize: SIZE.xs,
  color: SUBTLE,
  textTransform: 'uppercase',
  letterSpacing: '0.04em',
  mb: 0.5,
}

// ── Page Layout ─────────────────────────────────────────────────
export const pageContainerSx = {
  fontFamily: FONT,
  p: PAD.page,
  bgcolor: LIGHT_BG,
  minHeight: '100vh',
}

export const pageTitleSx = {
  fontFamily: FONT,
  fontWeight: WEIGHT.extra,
  fontSize: SIZE['2xl'],
  color: NAVY,
  lineHeight: 1.25,
}

export const sectionTitleSx = {
  fontFamily: FONT,
  fontWeight: WEIGHT.bold,
  fontSize: SIZE.xl,
  color: NAVY,
}

export const sectionCardSx = {
  p: PAD.card,
  borderRadius: '14px',
  border: `1px solid ${BORDER}`,
  bgcolor: WHITE,
}

// ── Table ────────────────────────────────────────────────────────
export const headerCellSx = {
  fontFamily: FONT,
  fontWeight: WEIGHT.bold,
  fontSize: SIZE.xs,
  textTransform: 'uppercase',
  color: SUBTLE,
  borderBottom: `2px solid ${BORDER}`,
  py: 1.2,
  px: 1.5,
  whiteSpace: 'nowrap',
  letterSpacing: '0.03em',
}

export const cellSx = {
  fontFamily: FONT,
  fontSize: SIZE.base,
  py: 1,
  px: 1.5,
  borderBottom: `1px solid ${BORDER}`,
}

// ── Buttons ──────────────────────────────────────────────────────
export const primaryBtnSx = {
  fontFamily: FONT,
  fontWeight: WEIGHT.semibold,
  fontSize: SIZE.xs,
  textTransform: 'none',
  whiteSpace: 'nowrap',
  bgcolor: PRIMARY_BLUE,
  color: WHITE,
  px: 2,
  py: 0.75,
  borderRadius: RADIUS.sm,
  '&:hover': {bgcolor: PRIMARY_BLUE, filter: 'brightness(0.9)'},
}

export const secondaryBtnSx = {
  fontFamily: FONT,
  fontWeight: WEIGHT.semibold,
  fontSize: SIZE.xs,
  textTransform: 'none',
  whiteSpace: 'nowrap',
  color: NAVY,
  border: `1px solid ${BORDER}`,
  px: 2,
  py: 0.75,
  borderRadius: RADIUS.sm,
  '&:hover': {borderColor: PRIMARY_BLUE, bgcolor: PRIMARY_BLUE + '08'},
}

// ── Badges ───────────────────────────────────────────────────────
export const badgeSx = (color) => ({
  fontFamily: FONT,
  fontWeight: WEIGHT.semibold,
  fontSize: SIZE['2xs'],
  px: 1,
  py: 0.25,
  borderRadius: '6px',
  bgcolor: color + '18',
  color,
  display: 'inline-block',
})

// ── Alert Strip ──────────────────────────────────────────────────
export const alertStripSx = (color) => ({
  fontFamily: FONT,
  fontSize: SIZE.sm,
  fontWeight: WEIGHT.semibold,
  px: 2,
  py: 1,
  borderRadius: RADIUS.md,
  bgcolor: color + '14',
  color: NAVY,
  border: `1px solid ${color}40`,
  display: 'flex',
  alignItems: 'center',
  gap: 1,
})

// ── Tab Bar ──────────────────────────────────────────────────────
export const tabBarSx = {
  minHeight: 42,
  borderBottom: `1px solid ${BORDER}`,
  '& .MuiTabs-indicator': {height: 3, borderRadius: '3px 3px 0 0', backgroundColor: PRIMARY_BLUE},
  '& .MuiTab-root': {
    textTransform: 'none', minHeight: 42, minWidth: 'auto', px: 2,
    fontFamily: FONT, fontSize: SIZE.xs, fontWeight: WEIGHT.semibold, color: SUBTLE,
  },
  '& .Mui-selected': {color: `${NAVY} !important`},
}

// ── Tab Container (wrapping box for TabList + TabPanels) ────────
export const tabContainerSx = {
  // bgcolor: WHITE,
  // border: `1px solid ${BORDER}`,
  // borderRadius: '12px',
  // overflow: 'hidden',
}

// ── Tab Panel ───────────────────────────────────────────────────
export const tabPanelSx = {
  // MUI TabPanel ships a default 24px padding — that's the phantom left/right
  // inset tabbed pages had vs non-tabbed pages (e.g. Channel Orders vs Forward
  // Shipments). Kill it; keep a small top gap below the tab bar.
  p: 0,
  pt: 2,
}

// ── Page Hero Banner ────────────────────────────────────────────
export const pageHeroSx = {
  background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%)',
  borderRadius: '14px', p: {xs: 2.5, md: 3}, mb: 2.5, position: 'relative', overflow: 'hidden',
  '&::after': {content: '""', position: 'absolute', top: -60, right: -60, width: 200, height: 200, borderRadius: '50%', background: 'rgba(55,131,231,0.12)', pointerEvents: 'none'},
  '&::before': {content: '""', position: 'absolute', bottom: -40, left: '30%', width: 160, height: 160, borderRadius: '50%', background: 'rgba(76,206,172,0.08)', pointerEvents: 'none'},
}
export const heroTitleSx = {
  color: WHITE, fontWeight: WEIGHT.bold, fontSize: {xs: '1.15rem', md: '1.35rem'}, fontFamily: FONT,
  lineHeight: 1.3, mb: 0.3, position: 'relative', zIndex: 1,
}
export const heroSubSx = {
  color: 'rgba(255,255,255,0.6)', fontSize: SIZE.xs, fontFamily: FONT,
  position: 'relative', zIndex: 1,
}

// ── Filter Bar ──────────────────────────────────────────────────
export const filterBarSx = {
  bgcolor: WHITE, border: `1px solid ${BORDER}`, borderRadius: '12px',
  p: PAD.card, mb: 2,
}

// ── Stepper ─────────────────────────────────────────────────────
export const stepperCardSx = {
  bgcolor: WHITE, border: `1px solid ${BORDER}`, borderRadius: '12px',
  p: {xs: 2.5, md: 3},
}
export const stepTitleSx = {
  fontFamily: FONT, fontWeight: WEIGHT.bold, fontSize: SIZE.lg,
  color: NAVY, mb: 2,
}

// ── Upload Section ──────────────────────────────────────────────
export const uploadSectionSx = {
  bgcolor: WHITE, border: `1px solid ${BORDER}`, borderRadius: '12px',
  p: PAD.card, mb: 2,
}

// ── Help Text (info bar) ────────────────────────────────────────
export const helpTextSx = {
  display: 'flex', gap: 1, p: 1.5, mb: 2, borderRadius: '8px',
  bgcolor: '#f0f4ff', border: '1px solid #c5d8f6',
}
export const helpTextTypeSx = {
  fontFamily: FONT, fontSize: SIZE['2xs'], color: '#455A64', lineHeight: 1.5,
}

// ── Data Grid wrapper ───────────────────────────────────────────
export const dataGridWrapSx = {
  bgcolor: WHITE, border: `1px solid ${BORDER}`, borderRadius: '12px',
  overflow: 'auto',
}

// ── Form button row ─────────────────────────────────────────────
export const btnRowSx = {
  display: 'flex', alignItems: 'center', gap: GAP.md, flexWrap: 'wrap',
}
